import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/CampaignCheckoutSessionLink.graphql';
import { Query, QuerycampaignCheckoutSessionLinkArgs } from '../../__generated__/globalTypes';

export const useCampaignCheckoutSessionLinkQuery = (
  options?: QueryHookOptions<Query, QuerycampaignCheckoutSessionLinkArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerycampaignCheckoutSessionLinkArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useCampaignCheckoutSessionLinkLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerycampaignCheckoutSessionLinkArgs>
): QueryResult<Query, QuerycampaignCheckoutSessionLinkArgs> & {
  getCampaignCheckoutSessionLink: LazyQueryExecFunction<Query, QuerycampaignCheckoutSessionLinkArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getCampaignCheckoutSessionLink, result] = useLazyQuery<Query, QuerycampaignCheckoutSessionLinkArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getCampaignCheckoutSessionLink, ...result };
};

export const useCampaignCheckoutSessionLinkPromiseQuery = () => {
  const client = useApolloClient();

  const getCampaignCheckoutSessionLink = useCallback(
    (options?: Omit<QueryOptions<QuerycampaignCheckoutSessionLinkArgs, Query>, 'query'>) =>
      client.query<Query, QuerycampaignCheckoutSessionLinkArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getCampaignCheckoutSessionLink };
};
// Suspense Query Hooks
export const useCampaignCheckoutSessionLinkSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerycampaignCheckoutSessionLinkArgs>
) => {
  const result = useSuspenseQuery<Query, QuerycampaignCheckoutSessionLinkArgs>(QUERY, {
    ...options
  });

  return result;
};
